import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, Send } from "lucide-react";
import { toast } from "sonner";

export default function ProductReviews({ slug, onRatingChange }) {
    const { user } = useAuth();
    const [reviews, setReviews] = useState([]);
    const [form, setForm] = useState({ rating: 5, title: "", comment: "", author_name: "" });
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);

    function load() {
        api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    }
    useEffect(() => { load(); }, [slug]);

    async function submit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post(`/products/${slug}/reviews`, form);
            toast.success("Review posted!");
            setForm({ rating: 5, title: "", comment: "", author_name: "" });
            setShowForm(false);
            load();
            if (onRatingChange) onRatingChange();
        } catch (err) {
            toast.error("Could not post review");
        } finally {
            setLoading(false);
        }
    }

    return (
        <section className="mt-16 pt-12 border-t border-border" data-testid="product-reviews">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="overline text-primary mb-2">Customer reviews</div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold">What buyers are saying</h2>
                </div>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary-hover"
                    data-testid="write-review-btn"
                >
                    {showForm ? "Cancel" : "Write a review"}
                </button>
            </div>

            {showForm && (
                <form onSubmit={submit} className="bg-surface border border-border p-6 mb-8" data-testid="review-form">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm font-semibold mr-2">Rating</span>
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button
                                key={n}
                                type="button"
                                onClick={() => setForm({ ...form, rating: n })}
                                data-testid={`rating-star-${n}`}
                            >
                                <Star className={`w-6 h-6 ${n <= form.rating ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                            </button>
                        ))}
                    </div>
                    {!user && (
                        <input
                            placeholder="Your name"
                            value={form.author_name}
                            onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                            className="w-full border border-border px-3 py-2 mb-3 focus:outline-none focus:border-primary"
                            data-testid="review-author"
                        />
                    )}
                    <input
                        required
                        placeholder="Review title"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="w-full border border-border px-3 py-2 mb-3 focus:outline-none focus:border-primary"
                        data-testid="review-title"
                    />
                    <textarea
                        required
                        placeholder="Share your honest opinion…"
                        rows={4}
                        value={form.comment}
                        onChange={(e) => setForm({ ...form, comment: e.target.value })}
                        className="w-full border border-border px-3 py-2 mb-3 focus:outline-none focus:border-primary"
                        data-testid="review-comment"
                    />
                    <button type="submit" disabled={loading} className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary-hover disabled:opacity-50" data-testid="review-submit">
                        <Send className="w-3.5 h-3.5" /> {loading ? "Posting…" : "Post review"}
                    </button>
                </form>
            )}

            {reviews.length === 0 ? (
                <div className="text-sm text-muted-foreground">No reviews yet. Be the first to share your thoughts.</div>
            ) : (
                <div className="space-y-6">
                    {reviews.map((r) => (
                        <div key={r.id} className="border-b border-border pb-6" data-testid={`review-${r.id}`}>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="flex">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-accent text-accent" : "text-muted-foreground/30"}`} />
                                    ))}
                                </div>
                                <span className="text-xs text-muted-foreground">{r.author_name} · {new Date(r.created_at).toLocaleDateString()}</span>
                            </div>
                            <h4 className="font-display font-bold mb-1">{r.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{r.comment}</p>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
